import { useEffect, useState } from "react";
import { BellRing, Check, ShieldAlert, Smartphone, Vibrate } from "lucide-react";
import { toast } from "sonner";
import { api, errText } from "../../connect/api";
import { nativeAlerts, setupNativeChannels } from "../../connect/push";
import { CEO_VIBE, VIBES, setVibeKey, vibrateFor } from "../../connect/sound";

/** Settings → vibration pattern picker (saved to the profile, applied to web push, in-app alerts and the Android channels). */
export const VibePicker = ({ prefs, onSaved }) => {
  const [saving, setSaving] = useState(false);
  const canVibrate = typeof navigator !== "undefined" && !!navigator.vibrate;
  const pick = async (id) => {
    vibrateFor("message", VIBES.find((v) => v.id === id)?.pattern || []);
    setSaving(true);
    try {
      const { data } = await api.put("/me/prefs", { vibe: id });
      setVibeKey(id);
      await setupNativeChannels(window.Capacitor?.Plugins?.PushNotifications).catch(() => {});
      onSaved(data.prefs);
      toast.success(id === "off" ? "Message vibration off" : `Vibration: ${VIBES.find((v) => v.id === id).label}`);
    } catch (err) { toast.error(errText(err)); } finally { setSaving(false); }
  };
  return (
    <div className="space-y-2" data-testid="settings-vibe-section">
      <div className="flex items-center gap-2 text-xs font-medium text-slate-100"><Vibrate size={14} className="text-blue-400" /> Vibration pattern for messages</div>
      <div className="grid grid-cols-2 gap-1.5">
        {VIBES.map((v) => {
          const active = (prefs.vibe || "gentle") === v.id;
          return (
            <button key={v.id} type="button" disabled={saving} onClick={() => pick(v.id)} data-testid={`settings-vibe-${v.id}`}
              className={`text-left rounded-lg border px-3 py-2 transition-colors ${active ? "border-blue-400/60 bg-blue-500/15" : "border-white/10 bg-white/[0.03] hover:bg-white/[0.06]"}`}>
              <div className="flex items-center justify-between text-xs font-medium text-slate-100">{v.label}{active && <Check size={13} className="text-blue-300" />}</div>
              <div className="text-[10px] text-slate-500">{v.hint}</div>
            </button>
          );
        })}
      </div>
      <div className="flex items-center gap-2 flex-wrap">
        <button type="button" onClick={() => vibrateFor("message")} className="nxc-btn nxc-btn-ghost h-8 text-xs" data-testid="settings-vibe-try">Try my pattern</button>
        <button type="button" onClick={() => vibrateFor("ceo", CEO_VIBE)} className="nxc-btn nxc-btn-ghost h-8 text-xs text-amber-300" data-testid="settings-vibe-try-ceo">Try CEO alert pattern</button>
      </div>
      <p className="text-[11px] text-slate-500">Tap a pattern to feel it and save it. CEO alerts always use a strong fixed pattern.{!canVibrate && " This browser cannot vibrate — the pattern still applies on your phone."}</p>
    </div>
  );
};

/** Settings → CEO alerts override Silent / Do Not Disturb (Android app only: alarm-class channel + notification-policy access). */
export const CeoOverride = () => {
  const NA = nativeAlerts();
  const [allowed, setAllowed] = useState(() => { try { return !!NA?.hasDndAccess(); } catch { return false; } });
  useEffect(() => {
    if (!NA) return;
    const check = () => { if (document.visibilityState === "visible") { try { const ok = !!NA.hasDndAccess(); setAllowed(ok); if (ok) NA.setupChannels(localStorage.getItem("nxc_vibe") || "gentle"); } catch {} } };
    document.addEventListener("visibilitychange", check);
    return () => document.removeEventListener("visibilitychange", check);
  }, [NA]);
  return (
    <div className="space-y-2 pt-2 border-t border-white/5" data-testid="settings-ceo-override">
      <div className="flex items-center gap-2 text-xs font-medium text-slate-100"><BellRing size={14} className="text-amber-300" /> CEO alerts ring through Silent / Do Not Disturb</div>
      {NA ? (
        <>
          <div className="flex items-center justify-between gap-3">
            <span className={`text-xs ${allowed ? "text-emerald-300" : "text-slate-300"}`} data-testid="settings-ceo-override-status">{allowed ? "Allowed — CEO alerts use the alarm volume and bypass Do Not Disturb." : "Not allowed yet — grant “Do Not Disturb access” to NexSecure Connect."}</span>
            {!allowed && <button type="button" onClick={() => { try { NA.requestDndAccess(); } catch {} }} className="nxc-btn nxc-btn-primary h-8 text-xs shrink-0" data-testid="settings-ceo-override-allow"><ShieldAlert size={13} /> Allow</button>}
          </div>
          <p className="text-[11px] text-slate-500">CEO alerts play on the alarm channel, so they ring even when the phone is on Silent or vibrate. Keep your alarm volume above zero. <button type="button" className="underline text-slate-400" onClick={() => { try { NA.openChannelSettings("nexsecure_ceo_v3"); } catch {} }} data-testid="settings-ceo-channel-settings">Open channel settings</button></p>
        </>
      ) : (
        <p className="text-[11px] text-slate-500 flex items-start gap-1.5"><Smartphone size={13} className="shrink-0 mt-0.5" /> Available in the NexSecure Connect Android app (v1.4 or later). Browsers cannot override the phone's silent mode — install the app to never miss a CEO alert.</p>
      )}
    </div>
  );
};
